/**
 * Hinweise aus der Übernahme des Altbestands — je Hinweis eine
 * Ein-Klick-Korrektur (nie automatisch).
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { cancelInvoice, createGutschrift, issueInvoiceFromOrder } from '@/lib/invoiceApi';

const LABELS = {
  betrag_abweichung: 'Gebuchter Betrag höher als Rechnung (Kundenrabatt nicht berücksichtigt)',
  storno_ohne_gegenbuchung: 'Storniert, aber ohne Gegenbuchung',
  nummer_ohne_buchung: 'Rechnungsnummer vergeben, aber nicht gebucht',
  kein_offener_posten: 'Gebuchte Rechnung ohne offenen Posten',
  zahlung_ohne_postenabgleich: 'Zahlungen höher als ausgeglichener Betrag',
};

function IssueRow({ issue, actor, onDone }) {
  const [busy, setBusy] = useState(false);
  const d = issue.details || {};
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  let action = null;
  if (issue.issue_type === 'betrag_abweichung' && Number(d.netto_differenz) > 0) {
    action = (
      <Button size="sm" variant="outline" disabled={busy}
        onClick={() => run(() => createGutschrift(Number(issue.ref_id), [{
          net: Number(d.netto_differenz), tax_rate: Number(d.steuersatz ?? 19), description: 'Korrektur Kundenrabatt',
        }], `Korrektur: Kundenrabatt bei ${d.invoice_no} nicht gebucht`, actor), `Korrektur-Gutschrift zu ${d.invoice_no} gebucht.`)}>
        Korrektur-Gutschrift {fmtEUR(d.differenz)}
      </Button>
    );
  } else if (issue.issue_type === 'storno_ohne_gegenbuchung') {
    action = (
      <Button size="sm" variant="outline" disabled={busy}
        onClick={() => run(() => cancelInvoice(Number(issue.ref_id), `Nachbuchung Storno vom ${String(d.storniert_am || '').slice(0, 10)}`, actor), `Stornorechnung zu ${d.invoice_no} erstellt.`)}>
        Stornorechnung erstellen
      </Button>
    );
  } else if (issue.issue_type === 'nummer_ohne_buchung') {
    action = (
      <Button size="sm" variant="outline" disabled={busy}
        onClick={() => run(() => issueInvoiceFromOrder(issue.ref_id, actor), `${d.invoice_no} gebucht.`)}>
        Jetzt buchen
      </Button>
    );
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2">
      <div className="min-w-0 text-sm">
        <span className="font-mono text-xs text-slate-700 mr-2">{d.invoice_no || issue.ref_id}</span>
        <span className="text-slate-900">{d.kunde || ''}</span>
        <span className="block text-xs text-slate-500">
          {LABELS[issue.issue_type] || issue.issue_type}
          {issue.issue_type === 'betrag_abweichung' && ` · gebucht ${fmtEUR(d.gebucht)}, richtig ${fmtEUR(d.korrekt)}`}
        </span>
      </div>
      <span className="flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {action}
      </span>
    </li>
  );
}

export default function MigrationIssues({ issues, actor, onDone }) {
  const [openList, setOpenList] = useState(false);
  if (!issues?.length) return null;
  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
      <button type="button" className="w-full flex items-center justify-between gap-2 text-left min-h-[44px]" onClick={() => setOpenList((v) => !v)}>
        <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="w-4 h-4" />
          {issues.length} Hinweis(e) aus der Übernahme der bisherigen Rechnungen — bitte prüfen
        </span>
        {openList ? <ChevronUp className="w-4 h-4 text-amber-900" /> : <ChevronDown className="w-4 h-4 text-amber-900" />}
      </button>
      {openList && (
        <ul className="mt-2 divide-y divide-amber-200 bg-white rounded-lg px-3">
          {issues.map((i) => <IssueRow key={i.id} issue={i} actor={actor} onDone={onDone} />)}
        </ul>
      )}
    </section>
  );
}
